<script>
/**
 * ****************************************************************************
 * @description     :   MNG Report Comment
 * @created at      :   2025/12/05
 * @created by      :   ANS903 – phuongdt@ans-asia.com
 * @package         :   Move
 * @copyright       :   Copyright (c) A.N.S Asia
 * @version         :   1.0.0
 * ****************************************************************************
 */

/**
 * Import template
 */
import template from './template.html';
import messages, { MSG_BUTTON, MSG_TITLE, MSG_TYPE } from '@/utils/messages';
import { mapActions, mapMutations, mapState } from 'vuex';
import store from '@/store';
import MNG_WorkReport002_store from '@/views/move/mng_workreport/MNG_WorkReport002/store';

/**
 * Define content of list component
 */
var mngReportComment = {
    template: template,
    props: {
        workReport: Object
    },
    beforeCreate() {
        if (!store.hasModule('MNG_WorkReport002')) {
            store.registerModule('MNG_WorkReport002', MNG_WorkReport002_store);
        }
    },
    emits: ['close-mb'],
    data() {
        return {
            comments: [],
            mentionUsers: [
                { name: '佐藤', avatar: '佐' },
                { name: '田中', avatar: '田' },
                { name: '鈴木', avatar: '鈴' }
            ],
            commentText: '',
            showMentionDropdown: false,
            editingId: null,
            editingText: '',
            isMobile: false,
            actionId: '',
            dropUpId: null,

            firstCommentNo: null,
            lastCommentNo: null,
            lastSyncTime: null,
            pollingTimer: null,
            pollingIntervalMs: 2000,
            isLoadingInitial: false,
            isLoadingOlder: false,
            isPollingSync: false,
            isSending: false,
            hasMoreOlderComments: true
        };
    },

    mounted() {
        this.isMobile = this.$route.name == 'APP_WorkReport001';
        document.addEventListener('click', this.handleClickOutside);
        this.initComments();
    },

    beforeUnmount() {
        document.removeEventListener('click', this.handleClickOutside);
        this.stopPolling();
    },
    computed: {
        ...mapState('app', ['user']),
        currentUserId() {
            const staffId = Number(this.user?.user_id);
            return Number.isNaN(staffId) ? null : staffId;
        },
        companyCd() {
            return this.workReport?.CompanyCD ?? 0;
        },
        workReportId() {
            return this.workReport?.WorkReportID ?? 0;
        },
        EmployeeCD() {
            return this.workReport?.EmployeeCD ?? 0;
        }
    },

    methods: {
        ...mapMutations('app', ['showModalMessage']),
        ...mapActions('MNG_WorkReport002', ['getComments', 'storeComment', 'deleteComment', 'getMentions']),

        initComments() {
            this.loadLatestComments(30);
            this.getMentions({
                params: {
                    CompanyCD: this.companyCd,
                    MDCD: this.workReport.MDCD,
                    OfficeCD: this.workReport.OfficeCD
                },
                callback: (rows) => {
                    this.mentionUsers = rows.map((x) => this.formatMention(x));
                }
            });
        },

        startPolling() {
            this.stopPolling();
            this.pollingTimer = setInterval(() => {
                this.pollSyncComments();
            }, this.pollingIntervalMs);
        },

        stopPolling() {
            if (this.pollingTimer) {
                clearInterval(this.pollingTimer);
                this.pollingTimer = null;
            }
        },

        getMeUserId() {
            return Number(this.currentUserId || 0);
        },

        formatMention(row) {
            const name = row.DisplayName || row.CommonName || row.StaffName || '';

            return {
                staffId: Number(row.StaffID ?? 0),
                employeeCd: Number(row.EmployeeCD ?? 0),
                name: name,
                avatar: name ? name.substring(0, 1) : ''
            };
        },

        formatComment(item) {
            const currentUserId = this.getMeUserId();
            const authorId = Number(item.AuthorCD ?? 0);
            const authorName = item.AuthorName || (authorId === currentUserId ? 'あなた' : `User ${authorId || ''}`.trim());

            const deleteFlag = Number(item.DeleteFlag ?? 0);
            const text = deleteFlag === 1 ? '削除されたコメントです。' : item.Content ?? '';

            return {
                id: Number(item.CommentNo ?? 0),
                commentNo: Number(item.CommentNo ?? 0),
                employeeCd: Number(item.EmployeeCD ?? 0),
                authorCd: authorId,
                name: authorName,
                avatar: authorName ? authorName.substring(0, 1) : '',
                text: text,
                rawText: item.Content ?? '',
                time: this.formatTime(item.MakeTime),
                side: authorId === currentUserId ? 'right' : 'left',
                makeTime: item.MakeTime ?? null,
                updateTime: item.UpdateTime ?? null,
                deleteTime: item.DeleteTime ?? null,
                deleteFlag: deleteFlag
            };
        },

        formatTime(value) {
            if (!value) return '';
            const dt = new Date(value);
            if (Number.isNaN(dt.getTime())) return '';
            return dt.toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit'
            });
        },

        updateCommentBoundaries() {
            if (!this.comments.length) {
                this.firstCommentNo = null;
                this.lastCommentNo = null;
                return;
            }

            this.comments.sort((a, b) => a.commentNo - b.commentNo);
            this.firstCommentNo = this.comments[0].commentNo;
            this.lastCommentNo = this.comments[this.comments.length - 1].commentNo;
        },

        getMaxUpdateTime(rows) {
            if (!rows || !rows.length) return this.lastSyncTime;

            let maxTime = this.lastSyncTime ? new Date(this.lastSyncTime) : null;

            rows.forEach((row) => {
                const value = row.UpdateTime || row.updateTime;
                if (!value) return;

                const dt = new Date(value);
                if (Number.isNaN(dt.getTime())) return;

                if (!maxTime || dt > maxTime) {
                    maxTime = dt;
                }
            });

            return maxTime ? maxTime.toISOString() : this.lastSyncTime;
        },
        mergeComments(rows) {
            if (!rows || !rows.length) return;

            const map = new Map(this.comments.map((x) => [x.commentNo, x]));

            rows.forEach((row) => {
                const formatted = this.formatComment(row);

                if (this.editingId === formatted.commentNo) {
                    return;
                }

                const oldItem = map.get(formatted.commentNo);
                map.set(formatted.commentNo, {
                    ...(oldItem || {}),
                    ...formatted
                });
            });

            this.comments = Array.from(map.values()).sort((a, b) => a.commentNo - b.commentNo);
            this.updateCommentBoundaries();
            this.lastSyncTime = this.getMaxUpdateTime(rows);
        },

        loadLatestComments(pageSize = 30) {
            if (this.isLoadingInitial) return;
            this.isLoadingInitial = true;

            this.getComments({
                params: {
                    companyCd: this.companyCd,
                    workReportId: this.workReportId,
                    mode: 'latest',
                    pageSize: pageSize
                },
                callback: (rows) => {
                    try {
                        this.comments = rows.map((x) => this.formatComment(x)).sort((a, b) => a.commentNo - b.commentNo);
                        this.updateCommentBoundaries();
                        this.hasMoreOlderComments = rows.length >= pageSize;
                        this.lastSyncTime = this.getMaxUpdateTime(rows);

                        this.$nextTick(() => {
                            this.scrollToBottom();
                        });

                        this.startPolling();
                    } finally {
                        this.isLoadingInitial = false;
                    }
                },
                onError: () => {
                    this.isLoadingInitial = false;
                }
            });
        },

        loadOlderComments() {
            if (this.isLoadingOlder || !this.hasMoreOlderComments || !this.firstCommentNo) return;

            const list = this.$refs.commentsList;
            const prevScrollHeight = list ? list.scrollHeight : 0;

            this.isLoadingOlder = true;

            this.getComments({
                params: {
                    companyCd: this.companyCd,
                    workReportId: this.workReportId,
                    mode: 'older',
                    commentNo: this.firstCommentNo,
                    pageSize: 30
                },
                callback: (rows) => {
                    try {
                        const older = rows.map((x) => this.formatComment(x)).sort((a, b) => a.commentNo - b.commentNo);

                        if (!older.length) {
                            this.hasMoreOlderComments = false;
                            return;
                        }

                        const map = new Map(this.comments.map((x) => [x.commentNo, x]));
                        older.forEach((x) => map.set(x.commentNo, x));

                        this.comments = Array.from(map.values()).sort((a, b) => a.commentNo - b.commentNo);
                        this.updateCommentBoundaries();

                        const maxSync = this.getMaxUpdateTime(rows);
                        if (maxSync) {
                            this.lastSyncTime = maxSync;
                        }

                        this.$nextTick(() => {
                            const currentList = this.$refs.commentsList;
                            if (!currentList) return;

                            const newScrollHeight = currentList.scrollHeight;
                            currentList.scrollTop = newScrollHeight - prevScrollHeight;
                        });
                    } finally {
                        this.isLoadingOlder = false;
                    }
                },
                onError: () => {
                    this.isLoadingOlder = false;
                }
            });
        },

        pollSyncComments() {
            if (this.isPollingSync || !this.lastSyncTime) return;
            this.isPollingSync = true;

            const list = this.$refs.commentsList;
            const isNearBottom = list ? list.scrollHeight - list.scrollTop - list.clientHeight < 80 : true;

            this.getComments({
                params: {
                    companyCd: this.companyCd,
                    workReportId: this.workReportId,
                    mode: 'sync',
                    lastSyncTime: this.lastSyncTime
                },
                callback: (rows) => {
                    try {
                        if (rows && rows.length) {
                            this.mergeComments(rows);

                            if (isNearBottom) {
                                this.$nextTick(() => {
                                    this.scrollToBottom();
                                });
                            }
                        }
                    } finally {
                        this.isPollingSync = false;
                    }
                },
                onError: () => {
                    this.isPollingSync = false;
                }
            });
        },

        handleCommentsScroll() {
            const list = this.$refs.commentsList;
            if (!list) return;

            if (list.scrollTop <= 20) {
                this.loadOlderComments();
            }
        },

        handleClickOutside(event) {
            const target = event.target;

            const mention = this.$refs.commentMention;
            const actionsRef = this.$refs.messageActions;

            let clickedInsideMention = false;
            if (mention) {
                clickedInsideMention = mention === target || mention.contains(target);
            }

            let clickedInsideActions = false;
            if (actionsRef) {
                if (Array.isArray(actionsRef)) {
                    clickedInsideActions = actionsRef.some((el) => el && (el === target || el.contains(target)));
                } else {
                    clickedInsideActions = actionsRef === target || actionsRef.contains(target);
                }
            }

            if (!clickedInsideMention) {
                this.showMentionDropdown = false;
            }

            if (!clickedInsideActions) {
                this.actionId = null;
                this.dropUpId = null;
            }
        },

        toggleAction(id) {
            if (this.actionId === id) {
                this.actionId = null;
                this.dropUpId = null;
                return;
            }

            this.actionId = id;

            this.$nextTick(() => {
                const root = this.$el.querySelector(`[data-id="${id}"]`);
                if (!root) return;

                const menu = root.querySelector('.message-menu');
                if (!menu) return;

                const rect = menu.getBoundingClientRect();
                const vpHeight = this.$refs.commentsList.getBoundingClientRect().height;

                if (rect.bottom > vpHeight) {
                    this.dropUpId = id;
                } else {
                    this.dropUpId = null;
                }
            });
        },

        /* === コメント送信 === */
        sendComment() {
            const text = this.commentText.trim();
            if (!text || this.isSending) return;

            this.isSending = true;

            this.storeComment({
                body: {
                    CompanyCD: this.companyCd,
                    WorkReportID: this.workReportId,
                    CommentNo: null,
                    EmployeeCD: Number(this.EmployeeCD),
                    AuthorCD: this.currentUserId,
                    Content: text,
                    P_user_id: this.currentUserId
                },
                callback: (row) => {
                    try {
                        this.mergeComments([row]);
                        this.commentText = '';
                        this.showMentionDropdown = false;

                        this.$nextTick(() => {
                            this.focusInputComment();
                            this.resetHeightInput('createInputComment');
                            this.scrollToBottom();
                        });
                    } finally {
                        this.isSending = false;
                    }
                },
                onError: () => {
                    this.isSending = false;
                }
            });
        },

        submitSendComment(e) {
            if (e.key === 'Enter') {
                this.sendComment();
            }
        },

        focusInputComment() {
            const el = document.getElementById('createInputComment');
            if (el) {
                el.focus();
            }
        },

        clearComment() {
            this.commentText = '';
            this.showMentionDropdown = false;
            this.focusInputComment();
        },

        /* === コメント削除 === */
        onDeleteComment(id) {
            this.showModalMessage({
                type: MSG_TYPE.CONFIRM,
                title: MSG_TITLE.C001,
                content: messages.C003,
                cancelText: MSG_BUTTON.cancelText,
                okText: MSG_BUTTON.okText,
                callback: (ok) => {
                    if (!ok) return;

                    this.deleteComment({
                        body: {
                            CompanyCD: this.companyCd,
                            WorkReportID: this.workReportId,
                            CommentNo: id,
                            P_user_id: this.currentUserId
                        },
                        callback: (row) => {
                            this.mergeComments([row]);
                            this.actionId = null;
                            this.dropUpId = null;
                        },
                        onError: () => {
                            this.actionId = null;
                            this.dropUpId = null;
                        }
                    });
                }
            });
        },

        /* === コメント編集 === */
        startEdit(comment) {
            if (comment.side !== 'right' || comment.deleteFlag === 1) return;
            this.editingId = comment.commentNo;
            this.editingText = comment.rawText || comment.text;

            this.autoResize('editInputComment');
            this.$nextTick(() => {
                const el = document.getElementById('editInputComment');
                if (el) {
                    el.focus();
                }
            });
            this.actionId = null;
            this.dropUpId = null;
        },

        saveEdit(comment) {
            const newText = this.editingText.trim();
            if (!newText) {
                this.editingId = null;
                this.editingText = '';
                this.resetHeightInput('editInputComment');
                return;
            }

            this.storeComment({
                body: {
                    CompanyCD: this.companyCd,
                    WorkReportID: this.workReportId,
                    CommentNo: comment.commentNo,
                    Content: newText,
                    P_user_id: this.currentUserId
                },
                callback: (row) => {
                    this.mergeComments([row]);
                    this.editingId = null;
                    this.editingText = '';
                    this.resetHeightInput('editInputComment');
                },
                onError: () => {
                    this.editingId = null;
                    this.editingText = '';
                    this.resetHeightInput('editInputComment');
                }
            });
        },

        isEditing(id) {
            return this.editingId === id;
        },

        /* === メンション === */
        onCommentKeyup(e) {
            this.autoResize('createInputComment');
            const value = e.target.value;
            const lastChar = value.slice(-1);

            if (lastChar === '@') {
                this.openMentionList();
            }
        },

        openMentionList() {
            this.showMentionDropdown = true;
        },

        selectMention(user) {
            this.commentText += user.name + ' ';
            this.showMentionDropdown = false;
            this.focusInputComment();
        },

        /* === スクロール === */
        scrollToBottom() {
            this.$nextTick(() => {
                const list = this.$refs.commentsList;
                if (list) {
                    list.scrollTop = list.scrollHeight + 10;
                }
            });
        },

        handleClose() {
            this.$emit('close-mb');
        },

        resetHeightInput(id) {
            const el = document.getElementById(id);
            if (!el) return;
            el.style.setProperty('overflow', `auto`, 'important');
            el.style.setProperty('height', `50px`, 'important');
        },

        autoResize(id) {
            this.$nextTick(() => {
                const el = document.getElementById(id);
                if (!el) return;

                const minHeight = 50;
                const maxHeight = this.isMobile ? 153 : 192;

                el.style.setProperty('height', 'auto', 'important');

                const scrollH = el.scrollHeight;
                const newHeight = Math.max(minHeight, Math.min(scrollH, maxHeight));

                el.style.setProperty('height', `${newHeight}px`, 'important');

                const overflowMode = scrollH > maxHeight ? 'auto' : 'hidden';
                el.style.setProperty('overflow', overflowMode, 'important');
            });
        }
    }
};

export default mngReportComment;
</script>
<style scoped lang="scss">
.card {
    background: #ffffff;
    border-radius: 7px;
    padding: 14px;
    border: 1px solid rgba(148, 163, 184, 0.25);
}

/* ==== コメント（左 30%） ==== */
.report-comments {
    flex: 0 0 30%;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    @media screen and (max-width: 768px) {
        height: calc(100vh - 150px);
    }
    @media screen and (min-width: 768px) {
        height: calc(100vh - 188px);
    }
}

.comments-list {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    @media screen and (max-width: 768px) {
        padding-right: 4px;
    }
    @media screen and (min-width: 768px) {
        padding-right: 17px;
    }
}

/* コメントアイテム */
.comment-item {
    margin-bottom: 16px;
    max-width: 85%;
    position: relative;
}

.comment-left {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}

.comment-right {
    margin-left: auto;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
}

/* 名前・アイコン */
.comment-header {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 4px;
}

.comment-avatar {
    width: 26px;
    height: 26px;
    background: #cbd5e1;
    border-radius: 50%;
    font-weight: bold;
    display: flex;
    justify-content: center;
    align-items: center;
}

.comment-name {
    font-size: 12px;
    font-weight: 600;
}

/* バルーン */
.bubble-left {
    background: #ffffff;
    border: 1px solid #d1d5db;
    padding: 8px 12px;
    border-radius: 16px 16px 16px 6px;
    font-size: 13px;
}

.bubble-right {
    background: #d1e8ff;
    padding: 8px 12px;
    border-radius: 16px 16px 6px 16px;
    font-size: 13px;
    max-width: 100%;
}

/* 時刻 */
.comment-time {
    font-size: 10px;
    margin-top: 4px;
    color: #6b7280;
}

/* ==== 編集・削除ボタン（下に配置） ==== */
.comment-actions-bottom-line {
    margin-top: 4px;
    display: none;
    gap: 10px;
    font-size: 12px;
}

.comment-item.comment-right:hover .comment-actions-bottom-line {
    display: flex;
    justify-content: flex-end;
}

.action-btn {
    cursor: pointer;
    opacity: 0.7;
}

.action-btn:hover {
    opacity: 1;
}

/* 編集モード */
.edit-textarea {
    width: 100%;
    padding: 6px;
    font-size: 13px;
    border-radius: 10px;
    border: 1px solid #d1d5db;
}

/* ==== メンション候補 UI ==== */
.mention-list {
    position: absolute;
    bottom: 55px;
    left: 0;
    background: white;
    border: 1px solid #d1d5db;
    box-shadow: 0 6px 20px rgba(15, 23, 42, 0.08);
    border-radius: 10px;
    width: 200px;
    z-index: 20;

    max-height: 200px;
    overflow-y: auto;
}

.mention-item {
    padding: 8px;
    cursor: pointer;
    border-bottom: 1px solid #eee;
}

.mention-item:hover {
    background: #eef6ff;
}
.mention-list::-webkit-scrollbar {
    width: 6px;
}

.mention-list::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
}
/* ==== 入力欄 ==== */
.comment-input-wrapper {
    border-top: 1px solid #d1d5db;
    padding-top: 8px;
    position: relative;
    flex-shrink: 0;
}

.comment-textarea {
    width: 97%;
    padding: 8px;
    min-height: 50px;
    border-radius: 12px !important;
    border: 1px solid #d1d5db !important;
    font-size: 13px;
    resize: none;
}

.comment-actions-bottom {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 6px;
}

.btn {
    width: 80px !important;
    height: 30px !important;
}
.btn-close {
    background: none;
    border: none;
    font-size: 18px;
    cursor: pointer;
    color: #666;
    display: flex;
    align-items: center;
    justify-content: end;
    border-radius: 4px;
}
.button_ok {
    margin-bottom: 0 !important;
}
.btn-clear {
    background-color: #6c757d !important;
    color: #fff !important;
}
.header-chat {
    border-bottom: thin solid rgba(0, 0, 0, 0.12);
    margin-bottom: 7px;
}
.bubble-right {
    max-width: 70%;
}
/* 三点リーダーボタン */
.message-actions {
    position: relative;
    height: 30px;
}

.menu-toggle {
    border: none;
    background: transparent;
    border-radius: 999px;
    width: 26px;
    height: 26px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;
    color: #64748b;
    font-size: 18px;
    line-height: 1;
    transition: background 0.15s ease, color 0.15s ease, transform 0.08s ease;
}

.menu-toggle:active {
    transform: scale(0.94);
}

.menu-toggle:hover {
    background: rgba(148, 163, 184, 0.16);
    color: #0f172a;
}

.message-row.self .menu-toggle {
    margin-left: 2px;
}
.message-row.other .menu-toggle {
    margin-right: 2px;
}
/* メニュー本体 */
.message-menu {
    position: absolute;
    top: 28px;
    right: 0;
    min-width: 120px;
    background: #ffffff;
    border-radius: 10px;
    box-shadow: 0 8px 20px rgba(15, 23, 42, 0.18);
    padding: 4px 0;
    font-size: 13px;
    display: none;
    z-index: 20;
}
.message-menu.drop-up {
    top: auto;
    bottom: 28px;
}

.message-menu.open {
    display: block;
}

.message-menu button {
    width: 100%;
    border: none;
    background: transparent;
    text-align: left;
    padding: 6px 10px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    color: #0f172a;
}

.message-menu button span.icon {
    width: 16px;
    text-align: center;
    font-size: 14px;
}

.message-menu button:hover {
    background: #f1f5f9;
}

.message-menu button.delete {
    color: #dc2626;
}
.message-menu button.delete:hover {
    background: #fef2f2;
}

.message-text.deleted {
    font-style: italic;
    color: #94a3b8;
}
</style>
